/**
 * Shared vocabulary for how a device sync reports itself.
 *
 * Progress used to be presented two different ways depending on which button started it:
 * the centered modal (SyncProgressContext → pages/SyncProgress) for zone sync and
 * "sincronizar tudo", a top-right `toast.loading` counter for the person modal footer,
 * PersonProfile and visitor registration. Same operation, two places to look.
 *
 * There is one surface now: a compact card in the toast corner that auto-dismisses like
 * any toast, with a zoom button that promotes it to the full modal (per-device list,
 * pause/stop, retry). Producers describe the run; the provider picks the surface.
 */

import { SyncProgressItem } from '../components/SyncProgressModal';

/** A clean run has nothing left to read — it goes about as fast as a normal toast. */
export const AUTO_DISMISS_CLEAN_MS = 6_000;
/** A run with failures stays long enough to be noticed and zoomed into. */
export const AUTO_DISMISS_FAILED_MS = 15_000;

/**
 * How long the card lingers after the run ends, or null while it is still running.
 * The zoomed modal is never auto-dismissed — someone is reading it.
 */
export function autoDismissDelayMs({ isComplete, fail }: { isComplete: boolean; fail: number }): number | null {
  if (!isComplete) return null;
  return fail > 0 ? AUTO_DISMISS_FAILED_MS : AUTO_DISMISS_CLEAN_MS;
}

export interface SyncSubject {
  id?: string;
  name: string;
  photoUrl?: string;
}

export interface PersonSyncItemInput {
  person: SyncSubject;
  deviceName: string;
  status: 'ok' | 'failed';
  error?: string;
}

/**
 * Builds a row for a person→device sync.
 *
 * The device name is the row TITLE because it is what varies line to line, but the row's
 * identity — avatar and click target — is the person. Rows built by hand here used to omit
 * `photoUrl`/`personId`, which left the modal rendering the first letter of the device name
 * as if it were an avatar.
 */
export function buildPersonSyncItem({
  person,
  deviceName,
  status,
  error,
}: PersonSyncItemInput): SyncProgressItem {
  return {
    label: deviceName,
    owner: person.name,
    status,
    ...(error ? { error } : {}),
    photoUrl: person.photoUrl,
    personId: person.id,
  };
}

/** Header line for a person→devices sync, e.g. "Gaspar Andrade — 6 dispositivo(s)". */
export function buildPersonSyncTitle(personName: string, deviceCount: number): string {
  return `${personName} — ${deviceCount} dispositivo(s)`;
}

export interface SyncHeadlineInput {
  isComplete: boolean;
  isStopped: boolean;
  ok: number;
  fail: number;
}

/**
 * Title shown at the top of the modal.
 *
 * "Sincronização concluída" over six red rows reads as success — a run where nothing
 * reached a device is not a conclusion, it is a failure, and it has to say so.
 */
export function syncHeadline({ isComplete, isStopped, ok, fail }: SyncHeadlineInput): string {
  if (!isComplete) return 'Sincronizando...';
  if (isStopped) return 'Sincronização interrompida';
  if (fail > 0 && ok === 0) return 'Nenhum dispositivo foi sincronizado';
  if (fail > 0) return 'Sincronização parcial';
  return 'Sincronização concluída';
}
